import React, { FC } from 'react';
import { useLocation } from 'react-router';
import { AlertProps } from 'antd/lib/alert';
import { Trans } from 'react-i18next';

import { useCommonErrors } from '~/controllers/validation';
import { useGetApplication } from '~/controllers/wizard';
import { Alert, Space, UnorderedList } from '~/components';
import { ErrorCircleIcon, WarningCircleIcon } from '~/img';
import { Stage } from '~/types/Application';
import { ApplicationPayload } from '~/types/ApplicationPayload';
import { getStagePayload } from '~/utils/getStagePayload';
import { useTranslationContext, useTranslationKeys } from '~/controllers/translation';
import features from '~/constants/features';

interface Props {
  formName?: string;
}

const ApplicationCommonErrors: FC<Props> = (props) => {
  const { formName } = props;
  const { t } = useTranslationContext('application');
  const translationKeys = useTranslationKeys();

  const applicationQuery = useGetApplication();
  // const resubmitMessagesRaw =
  //   (applicationQuery.data?.stage === Stage.uaeFillApplication
  //     ? applicationQuery.data.stagePayload.activeStep.errorInformation?.errorMessages
  //     : applicationQuery.data?.stage === Stage.contract
  //     ? applicationQuery.data?.errorInformation?.errorMessages
  //     : applicationQuery.data?.stage === Stage.payment
  //     ? applicationQuery.data?.errorInformation?.errorMessages
  //     : undefined) || [];
  const currentStage = applicationQuery.data?.stage;
  const payloadKey = getStagePayload.wizard(applicationQuery.data)?.activeStep?.payloadKey;
  const resubmitMessages =
    applicationQuery?.data?.payload?.errorInfo?.comments?.filter(({ key, type }) => {
      const errorPayloadKey = key.substring(0, key.indexOf('.'));
      const errorStage = getStageByPayloadKey(errorPayloadKey as any);
      return (
        currentStage === errorStage &&
        (payloadKey ? errorPayloadKey === payloadKey : true) &&
        type === 'WARN'
      );
    }) ?? [];
  const hasErrorMessage = !!resubmitMessages.length;

  const location = useLocation();
  const resultFormName = formName || location.pathname;
  const [commonErrors] = useCommonErrors(resultFormName);

  const thiqahError: CommonErrorConfig = {
    header: t('Something went wrong'),
    description: (
      <div>
        {t(
          'You can’t proceed with this application due to data validation errors. Update the data placed in Thiqah and create a new application.'
        )}
        <br /> <br />
        <Trans
          t={t}
          i18nKey="Your will able to restart the onboarding process after the application is terminated automatically on the next day. In case of any questions contact <1>Customer support</1>."
        >
          {
            'Your will able to restart the onboarding process after the application is terminated automatically on the next day. In case of any questions contact '
          }
          <a href={features.contactUsLink} target="__blank">
            Customer support
          </a>
          .
        </Trans>
      </div>
    ),
    alertType: 'error',
  };

  const mobileVerificationError: CommonErrorConfig = {
    header: t('Please, fix application data'),
    description: (
      <Trans
        t={t}
        i18nKey="Please correct the authorized signatory’s Phone Number: The provided phone number is invalid according to the National Information Center's data. Ensure the phone number is entered correctly. If you have any questions, please contact <1>Customer support</1>."
      >
        {
          "Please correct the authorized signatory’s Phone Number: The provided phone number is invalid according to the National Information Center's data. Ensure the phone number is entered correctly. If you have any questions, please contact "
        }
        <a href={features.contactUsLink} target="__blank">
          Customer support
        </a>
      </Trans>
    ),
    alertType: 'warning',
  };

  const mobileVerificationNotOwnerError: CommonErrorConfig = {
    header: t('Please, fix application data'),
    description: translationKeys.application === 'ksaApplication' ? (
      <Trans ns="ksaApplication" i18nKey="MOBILE_VERIFICATION_NOT_OWNER">
        Mobile number is not associated with the provided National ID / Iqama
      </Trans>
    ) : (
      <>
        {t(
          "The provided phone number does not match the one listed in the National Information Center's record for the authorised signatory’s document. Change the phone number to the correct one."
        )}
      </>
    ),
    alertType: 'warning',
  };

  const internalWarning: CommonErrorConfig = {
    header: t('Something went wrong'),
    description: t('You can’t proceed with this application due to data validation errors.'),
    alertType: 'error',
  };
  return (
    <>
      {(commonErrors?.[resultFormName] || hasErrorMessage) && (
        <Space direction="vertical" size={8}>
          {hasErrorMessage && (
            <Alert
              message={t('Correct application data')}
              description={
                resubmitMessages.length === 1 ? (
                  resubmitMessages[0]?.description
                ) : (
                  <UnorderedList values={resubmitMessages?.map(({ description }) => description)} />
                )
              }
              type="warning"
              icon={<WarningCircleIcon />}
              showIcon
            />
          )}
          {commonErrors[resultFormName]?.map((item) => {
            const { header, description, alertType }: CommonErrorConfig =
              item.name[0] === 'COMMON'
                ? {
                    header: t('Please, fix application data'),
                    description: item.errors[0],
                    alertType: 'warning',
                  }
                : item.name[0] === 'THIQAH'
                ? thiqahError
                : item.name[0] === 'MOBILE'
                ? mobileVerificationError
                : item.name[0] === 'MOBILE_NOT_OWNER'
                ? mobileVerificationNotOwnerError
                : internalWarning;

            // You can’t proceed with this application due to license expiry. Update the data placed in Thiqah and create a new application.
            // Your will able to restart the onboarding process after the application is terminated automatically on the next day. In case of any questions contact Customer Support.

            if (hasErrorMessage && item.name[0] === 'shareholders') return null; // shareholders screen has form items in drawer and they are hidden on submit by design.
            // Line above is prevent redundant warnings on submit during resubmit
            return (
              // item.errors has only one element. Array has used for Antd Forms compatibility reasons
              <Alert
                key={item.name.join('.')}
                message={header}
                description={description}
                type={alertType}
                icon={alertType === 'warning' ? <WarningCircleIcon /> : <ErrorCircleIcon />}
                showIcon
              />
            );
          })}
        </Space>
      )}
    </>
  );
};

const getStageByPayloadKey = (payloadKey: keyof ApplicationPayload): Stage | undefined => {
  switch (payloadKey) {
    case 'companyInformation':
    case 'address':
    case 'managers':
    case 'shareholders':
    case 'companyQuestionnaire':
    case 'bankAccount':
      return Stage.ksaApplication;
    case 'services':
      return Stage.ksaServices;
    case 'contract':
      return Stage.contract;
    case 'paymentDetails':
      return Stage.payment;
    default:
      return undefined;
  }
};

type CommonErrorConfig = {
  header: string;
  description: React.ReactNode;
  alertType: AlertProps['type'];
};

export default ApplicationCommonErrors;
